import { useEffect, useState } from 'react'
import { styled } from '@linaria/react'

/**
 * Detects when a newer build has been deployed while this tab stayed open, and
 * offers a one-tap refresh. Without it, a redeploy leaves the tab running stale
 * JS against a moved backend — the live projection can't reconnect, so new
 * writes (e.g. adding a meal) don't reflect until a manual reload, and the user
 * is acting on a stale view without knowing it.
 *
 * The signal is the hashed entry bundle Vite emits in index.html. nginx serves
 * index.html as `no-cache`, so re-fetching it and comparing the `/assets/index-*`
 * hash to the one this tab booted from is a reliable "a new version is live"
 * check that needs no backend endpoint. In dev there is no hashed bundle, so the
 * watcher is inert.
 */
const ENTRY_RE = /<script[^>]*type="module"[^>]*src="([^"]*\/assets\/index-[^"]+)"/i

const bootedEntry = (): string | null => {
  if (typeof document === 'undefined') return null
  const el = document.querySelector('script[type="module"][src*="/assets/index-"]')
  return el?.getAttribute('src') ?? null
}

const fetchLatestEntry = async (): Promise<string | null> => {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const html = await res.text()
    return ENTRY_RE.exec(html)?.[1] ?? null
  } catch {
    // Offline or transient — say nothing; the next tick retries.
    return null
  }
}

/** True once a different entry bundle is being served than the one we booted. */
export const useDeployWatcher = (intervalMs = 60_000): boolean => {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    const booted = bootedEntry()
    // No hashed bundle (dev server) — nothing to watch.
    if (!booted) return
    let alive = true

    const check = async () => {
      if (!alive || stale) return
      const latest = await fetchLatestEntry()
      if (alive && latest && latest !== booted) setStale(true)
    }

    const timer = window.setInterval(check, intervalMs)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    void check()

    return () => {
      alive = false
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
    // `stale` intentionally omitted: once true we stop polling via the guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs])

  return stale
}

const Bar = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: calc(100vw - 24px);
  padding: 12px 14px 12px 18px;
  border-radius: 999px;
  background: #24160f;
  color: #fff;
  box-shadow: 0 12px 32px -8px rgba(20, 10, 5, 0.5);
  font-size: 0.875rem;
  animation: dep-rise 0.28s ease-out;

  @keyframes dep-rise {
    from {
      transform: translate(-50%, 12px);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }
`

const Text = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Refresh = styled.button`
  flex-shrink: 0;
  border: 0;
  cursor: pointer;
  padding: 7px 16px;
  border-radius: 999px;
  background: #fff;
  color: #24160f;
  font: inherit;
  font-weight: 600;

  &:hover {
    filter: brightness(0.94);
  }
`

/**
 * App-level banner. Mount once near the root; it renders nothing until a new
 * deploy is detected, then invites a refresh.
 */
export const DeployBanner = () => {
  const stale = useDeployWatcher()
  if (!stale) return null
  return (
    <Bar role="status" aria-live="polite">
      <Text>A new version of ChooseChow is available.</Text>
      <Refresh type="button" onClick={() => window.location.reload()}>
        Refresh
      </Refresh>
    </Bar>
  )
}
